import React, { useState } from 'react';

// One machine's runs, now and after a change, drawn to scale (the director,
// 15 Oct 2026: "proportionally long blocks, with order number W47, product
// name, duration 3 days, with the weeks on top"). The same look as the
// "Can we take this order?" answer: weeks across the top, a Now row and an
// After row, one block per run.
//
// Drawn around what changes, not around everything that moves: runs reaching
// past either edge are cut there (‹ ›), weekends are narrow -- a block's
// length is its working days -- and the frozen days, which the app moves
// nothing into, are hatched, so an empty day after "bring forward" has its
// reason on the picture (Week 48 Deckel, 15 Oct 2026).
//
// lane: { machine, window, gap, frozen, now: [block], after_leave: [block],
//         after_forward: [block] | null }   (backend utils/stockBoard.js)
// choice: 'forward' draws after_forward, anything else after_leave.
//
// The Move window (21 Oct 2026, backend utils/moveFrom.js) adds, all optional:
// max_days (a long move needs a wider stretch), mine_label / moved_label /
// off_label / arrives_label for the key, blocks that 'arrive' from another
// machine, and late: a run that would miss its cut-off, outlined red.

const TEAL   = '#14b8a6';
const AMBER  = '#f59e0b';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#e5e7eb';
const DIM    = '#9ca3af';
const OTHER  = '#3f4a5a';
const RED    = '#ef4444';
const WEEKEND = 0.3;          // a weekend day's width, against a working day's 1
const MAX_DAYS = 28;          // the longest stretch drawn: four weeks
const HATCH = 'repeating-linear-gradient(135deg, rgba(147,197,253,0.16) 0 4px, transparent 4px 9px)';

function addDaysStr(d, n) {
  const x = new Date(d + 'T12:00:00');
  x.setDate(x.getDate() + n);
  return x.toISOString().slice(0, 10);
}
function daysBetweenStr(a, b) {
  return Math.round((new Date(b + 'T12:00:00') - new Date(a + 'T12:00:00')) / 86400000);
}
function isWeekendStr(d) {
  const day = new Date(d + 'T12:00:00').getDay();
  return day === 0 || day === 6;
}
export function fmtDay(d) {
  if (!d) return '—';
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtShort(d) {
  return new Date(d + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
export function fmtSpan(a, b) {
  return !b || a === b ? fmtDay(a) : `${fmtDay(a)} – ${fmtDay(b)}`;
}
function isoWeekOf(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const w1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7);
}

const nameOf = (b) => [b.order, b.product || b.part].filter(Boolean).join(' · ') || 'A run';
const partOf = (b) => (b.product && b.part ? b.part : null);
const daysWord = (n) => `${n} day${n === 1 ? '' : 's'}`;
const overlaps = (a, b) => a.from <= b.to && b.from <= a.to;

function look(b) {
  if (b.change === 'off') return { bg: 'transparent', fg: TEAL, border: `1.5px dashed ${TEAL}` };
  if (b.late) return { bg: AMBER, fg: '#2a1a02', border: `2px solid ${RED}` };
  if (b.change === 'moved' || b.change === 'arrives') return { bg: AMBER, fg: '#2a1a02' };
  if (b.mine) return { bg: TEAL, fg: '#06231f' };
  if (b.status === 'completed') return { bg: '#2b323d', fg: DIM };
  return { bg: OTHER, fg: '#c6cfdb' };
}

function tipOf(b, lane) {
  const what = [b.order, b.product, b.part].filter(Boolean).join(' · ');
  const when = fmtSpan(b.from, b.to);
  const late = b.late ? ' · misses its cut-off' : '';
  if (b.change === 'off') return `${what} · ${when} · ${lane.off_label || 'comes off the board'}`;
  if (b.change === 'arrives') return `${what} · ${when} · ${lane.arrives_label || 'comes here'}${late}`;
  if (b.change === 'moved') return `${what} · moves ${b.was_from && b.was_from < b.from ? '' : 'up '}from ${fmtSpan(b.was_from, b.was_to)} to ${when} · ${daysWord(b.days)}${late}`;
  if (b.change === 'shorter') return `${what} · ${when} · ${daysWord(b.days)} (shorter)`;
  return `${what} · ${when} · ${daysWord(b.days)}${b.status === 'completed' ? ' · finished' : b.status === 'started' ? ' · running' : ''}`;
}

// The stretch to draw: from a little before the first thing that changes, to
// the end of what is drawn -- at most MAX_DAYS.
function windowOf(lane, after, maxDays = MAX_DAYS) {
  const changed = [
    ...lane.now.filter(b => b.mine).map(b => b.from),
    ...after.filter(b => b.change).map(b => b.from),
    ...(lane.gap ? [lane.gap.from] : []),
  ].sort();
  const all = [...lane.now, ...after];
  const firstAny = all.map(b => b.from).sort()[0] || lane.window.from;
  const focus = changed[0] || firstAny;
  // A few days of what runs before it, cut at the edge; or a day's margin
  // when nothing does.
  const from = [addDaysStr(focus, -3), addDaysStr(firstAny, -1)].sort().pop();
  const lastAny = all.map(b => b.to).sort().pop() || lane.window.to;
  const cap = addDaysStr(from, maxDays);
  const to = addDaysStr(lastAny < cap ? lastAny : cap, 1);
  return { from, to };
}

export default function RunsPicture({ lane, choice }) {
  const [hover, setHover] = useState(null);
  if (!lane) return null;
  const after = choice === 'forward' && lane.after_forward ? lane.after_forward : lane.after_leave;
  // One window for both choices, so switching does not rescale the drawing.
  const win = windowOf(lane, [...lane.after_leave, ...(lane.after_forward || [])], lane.max_days || MAX_DAYS);
  const { from, to } = win;
  const span = daysBetweenStr(from, to) + 1;
  if (span < 1 || span > 400) return null;

  // Each day's place along the row, weekends narrow.
  const days = [];
  for (let i = 0; i < span; i++) days.push(addDaysStr(from, i));
  const weight = (d) => (isWeekendStr(d) ? WEEKEND : 1);
  const start = new Map();
  let total = 0;
  for (const d of days) { start.set(d, total); total += weight(d); }
  const clampDay = (d) => (d < from ? from : d > to ? to : d);
  const x = (d) => (start.get(clampDay(d)) / total) * 100;
  const w = (a, b) => ((start.get(clampDay(b)) + weight(clampDay(b)) - start.get(clampDay(a))) / total) * 100;

  const mondays = days.filter(d => new Date(d + 'T12:00:00').getDay() === 1);
  // The first day gets a label of its own only where it does not run into the
  // first Monday's -- on a long move a few days are a sliver (21 Oct 2026).
  const labels = mondays.length && daysBetweenStr(from, mondays[0]) >= 3 && x(mondays[0]) >= 6 ? [from, ...mondays] : (mondays.length ? mondays : [from]);
  const weekends = days.filter(isWeekendStr);
  const frozenDays = lane.frozen ? days.filter(d => d >= lane.frozen.from && d <= lane.frozen.to && !isWeekendStr(d)) : [];

  const Row = ({ title, items }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <div style={{ width: 44, flexShrink: 0, fontSize: 9.5, fontWeight: 700, letterSpacing: '0.07em',
                    textTransform: 'uppercase', color: MUTED, textAlign: 'right' }}>{title}</div>
      <div style={{ position: 'relative', flex: 1, height: 42, background: '#12161c', borderRadius: 4, overflow: 'hidden' }}>
        {weekends.map(d => (
          <div key={d} style={{ position: 'absolute', left: `${x(d)}%`, width: `${w(d, d)}%`, top: 0, bottom: 0, background: '#0b0e12' }} />
        ))}
        {frozenDays.map(d => (
          <div key={`f${d}`} style={{ position: 'absolute', left: `${x(d)}%`, width: `${w(d, d)}%`, top: 0, bottom: 0, background: HATCH }} />
        ))}
        {mondays.map(m => (
          <div key={m} style={{ position: 'absolute', left: `${x(m)}%`, top: 0, bottom: 0, width: 1, background: '#2a323d' }} />
        ))}
        {items.filter(b => b.to >= from && b.from <= to).map(b => {
          const s = look(b);
          const cutL = b.from < from;
          const cutR = b.to > to;
          const width = w(b.from, b.to);
          // An outline something else now sits on shows no name: the run on it says what is there.
          const covered = b.change === 'off' && items.some(o => o !== b && o.change !== 'off' && overlaps(o, b));
          const showText = !covered && width > 2.6;
          // Narrow: the order and its days, nothing that would only be cut off.
          const narrow = width < 6;
          return (
            <div key={b.key}
              onMouseEnter={() => setHover({ b, row: title })}
              onMouseLeave={() => setHover(h => (h && h.b.key === b.key ? null : h))}
              style={{
                position: 'absolute', left: `calc(${x(b.from)}% + ${cutL ? 0 : 1}px)`, width: `calc(${width}% - ${(cutL ? 0 : 1) + (cutR ? 0 : 1)}px)`,
                top: 3, bottom: 3, zIndex: b.change === 'off' ? 1 : 2, overflow: 'hidden',
                borderRadius: `${cutL ? 0 : 4}px ${cutR ? 0 : 4}px ${cutR ? 0 : 4}px ${cutL ? 0 : 4}px`,
                background: s.bg, border: s.border || 'none', boxSizing: 'border-box',
                display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: `0 ${cutR ? 12 : narrow ? 2 : 5}px 0 ${cutL ? 12 : narrow ? 2 : 5}px`, textAlign: narrow ? 'center' : 'left',
                color: s.fg, lineHeight: 1.25, cursor: 'default',
                outline: hover && hover.b.key === b.key && hover.row === title ? '2px solid #ffffff66' : 'none',
              }}>
              {cutL && <span style={{ position: 'absolute', left: 3, top: '50%', transform: 'translateY(-50%)', fontSize: 11, opacity: 0.7 }}>‹</span>}
              {cutR && <span style={{ position: 'absolute', right: 3, top: '50%', transform: 'translateY(-50%)', fontSize: 11, opacity: 0.7 }}>›</span>}
              {showText && (
                <div style={{ fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {b.change === 'off' ? (narrow ? (lane.off_label ? '→' : 'off') : `${b.order || ''} ${lane.off_label || 'comes off'}`.trim()) : narrow ? (b.order || '·') : nameOf(b)}
                </div>
              )}
              {showText && b.change !== 'off' && (
                <div style={{ fontSize: 9.5, fontWeight: 600, opacity: 0.85, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {narrow ? `${b.days} d` : [daysWord(b.days), partOf(b)].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  const moved = after.some(b => b.change === 'moved');
  const off = after.some(b => b.change === 'off');
  const arrives = after.some(b => b.change === 'arrives');
  const late = after.some(b => b.late);
  const cut = [...lane.now, ...after].some(b => b.from < from || b.to > to);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 5 }}>
        <div style={{ width: 44, flexShrink: 0 }} />
        <div style={{ position: 'relative', flex: 1, height: 27 }}>
          {labels.map(m => (x(m) > 93 ? null : (
            <div key={m} style={{ position: 'absolute', left: `${x(m)}%`, whiteSpace: 'nowrap', paddingLeft: 3, lineHeight: 1.3 }}>
              <div style={{ fontSize: 9.5, color: MUTED, fontWeight: 700, letterSpacing: '0.04em' }}>Wk {isoWeekOf(m)}</div>
              <div style={{ fontSize: 10.5, color: DIM, fontVariantNumeric: 'tabular-nums' }}>{fmtShort(m)}</div>
            </div>
          )))}
        </div>
      </div>
      <Row title="Now" items={lane.now} />
      <Row title="After" items={after} />

      {hover && (
        <div style={{ position: 'absolute', left: `calc(54px + ${Math.min(x(hover.b.from < from ? from : hover.b.from), 55)}%)`, bottom: '100%', marginBottom: 4,
                      background: '#0b0e12', color: TEXT, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '6px 9px',
                      fontSize: 11.5, whiteSpace: 'nowrap', zIndex: 10, pointerEvents: 'none', boxShadow: '0 6px 20px rgba(0,0,0,0.6)' }}>
          {tipOf(hover.b, lane)}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6, marginLeft: 54, fontSize: 10.5, color: MUTED }}>
        {lane.now.some(b => b.mine) && <Key bg={TEAL} label={lane.mine_label || 'this line'} />}
        {off && <Key dashed label={lane.off_label || 'comes off the board'} />}
        {moved && <Key bg={AMBER} label={lane.moved_label || 'moves up'} />}
        {arrives && <Key bg={AMBER} label={lane.arrives_label || 'comes here'} />}
        {late && <Key bg={AMBER} ring={RED} label="misses its cut-off" />}
        {lane.now.some(b => !b.mine) && <Key bg={OTHER} label="other runs on this machine" />}
        {frozenDays.length > 0 && <Key hatch label="frozen days: the app moves nothing into them" />}
        <Key bg="#0b0e12" label="weekend (drawn narrow)" />
        {cut && <span>‹ › the run goes on past the edge</span>}
      </div>
    </div>
  );
}

function Key({ bg, dashed, hatch, ring, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <i style={{ width: 11, height: 11, borderRadius: 2, display: 'inline-block', boxSizing: 'border-box',
                  background: dashed ? 'transparent' : hatch ? HATCH : bg,
                  border: dashed ? `1.5px dashed ${TEAL}` : ring ? `2px solid ${ring}` : `1px solid ${BORDER}` }} />
      {label}
    </span>
  );
}

// What bringing forward moves, one run a line, and the frozen days when they
// leave part of the gap free -- easier to read than one long sentence.
export function MovedList({ lane }) {
  const moved = (lane?.after_forward || []).filter(b => b.change === 'moved');
  if (!moved.length) return lane?.forward?.says || null;
  return (
    <>
      {moved.map(b => (
        <span key={b.key} style={{ display: 'block' }}>
          {[b.order, b.product, b.part].filter(Boolean).join(' · ')}: {fmtSpan(b.was_from, b.was_to)} → <strong style={{ color: '#d1d5db' }}>{fmtSpan(b.from, b.to)}</strong>
        </span>
      ))}
      {lane.forward?.frozen_note && <span style={{ display: 'block', color: '#93c5fd', marginTop: 2 }}>{lane.forward.frozen_note}</span>}
    </>
  );
}
